import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Target,
  Users,
  Mail,
  Plus,
  X,
  Send,
  FileText,
  Brain,
  ArrowRight,
  RefreshCw,
  Loader2,
  ChevronRight,
  CheckCircle2,
  XCircle,
  BarChart3,
  Eye,
  UserCog,
} from "lucide-react";

export default function LeadershipBrief() {
  // State
  const [activeTab, setActiveTab] = useState("brief");
  const [loading, setLoading] = useState(false);
  const [preScrumBrief, setPreScrumBrief] = useState(null);
  const [weeklyReview, setWeeklyReview] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [scrumNotes, setScrumNotes] = useState("");
  const [scrumResult, setScrumResult] = useState(null);
  const [newRecipientEmail, setNewRecipientEmail] = useState("");
  const [newRecipientName, setNewRecipientName] = useState("");
  const [effortMisallocation, setEffortMisallocation] = useState([]);
  const [learningInsights, setLearningInsights] = useState(null);
  const [sendingBrief, setSendingBrief] = useState(false);

  // Fetch functions
  const fetchPreScrumBrief = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/rex-cadence/pre-scrum-brief`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setPreScrumBrief(data);
      }
    } catch (error) {
      console.error("Error fetching pre-scrum brief:", error);
      toast.error("Failed to load pre-scrum brief");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWeeklyReview = useCallback(async () => {
    try {
      const res = await fetch(`${API}/rex-cadence/weekly-review`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setWeeklyReview(data);
      }
    } catch (error) {
      console.error("Error fetching weekly review:", error);
    }
  }, []);

  const fetchRecipients = useCallback(async () => {
    try {
      const res = await fetch(`${API}/rex-cadence/leadership-recipients`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data);
      }
    } catch (error) {
      console.error("Error fetching recipients:", error);
    }
  }, []);

  const fetchEffortMisallocation = useCallback(async () => {
    try {
      const res = await fetch(`${API}/rex-cadence/effort-misallocation`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setEffortMisallocation(data.misallocations || []);
      }
    } catch (error) {
      console.error("Error fetching effort misallocation:", error);
    }
  }, []);

  const fetchLearningInsights = useCallback(async () => {
    try {
      const res = await fetch(`${API}/rex-cadence/learning-insights`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setLearningInsights(data);
      }
    } catch (error) {
      console.error("Error fetching learning insights:", error);
    }
  }, []);

  useEffect(() => {
    fetchPreScrumBrief();
    fetchRecipients();
    fetchEffortMisallocation();
    fetchLearningInsights();
  }, [fetchPreScrumBrief, fetchRecipients, fetchEffortMisallocation, fetchLearningInsights]);

  useEffect(() => {
    if (activeTab === "weekly") {
      fetchWeeklyReview();
    }
  }, [activeTab, fetchWeeklyReview]);

  // Actions
  const handleSendBrief = async () => {
    if (recipients.length === 0) {
      toast.error("Add at least one recipient first");
      return;
    }
    try {
      setSendingBrief(true);
      const res = await fetch(`${API}/rex-cadence/pre-scrum-brief/send`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Brief sent to ${data.sent_count} recipient(s)`);
      } else {
        toast.error(data.error || "Failed to send brief");
      }
    } catch (error) {
      toast.error("Failed to send brief");
    } finally {
      setSendingBrief(false);
    }
  };

  const handleCaptureScrum = async () => {
    if (!scrumNotes.trim()) {
      toast.error("Enter scrum notes first");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API}/rex-cadence/post-scrum`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ notes: scrumNotes }),
      });
      const data = await res.json();
      if (data.success) {
        setScrumResult(data);
        toast.success("Scrum outcomes captured successfully");
        setScrumNotes("");
      } else {
        toast.error(data.error || "Failed to capture scrum outcomes");
      }
    } catch (error) {
      toast.error("Failed to capture scrum outcomes");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecipient = async () => {
    if (!newRecipientEmail.trim()) {
      toast.error("Enter an email address");
      return;
    }
    try {
      const res = await fetch(`${API}/rex-cadence/leadership-recipients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: newRecipientEmail,
          name: newRecipientName,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setNewRecipientEmail("");
        setNewRecipientName("");
        fetchRecipients();
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error("Failed to add recipient");
    }
  };

  const handleRemoveRecipient = async (email) => {
    try {
      const res = await fetch(
        `${API}/rex-cadence/leadership-recipients/${encodeURIComponent(email)}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        fetchRecipients();
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error("Failed to remove recipient");
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="leadership-brief-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leadership Brief</h1>
            <p className="text-slate-600 mt-1">
              Rex Operating Cadence - Firm-level execution support
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPreScrumBrief}
              disabled={loading}
              data-testid="refresh-brief-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={handleSendBrief}
              disabled={sendingBrief || recipients.length === 0}
              data-testid="send-brief-btn"
            >
              {sendingBrief ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Brief
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="brief" data-testid="tab-brief">
            <Calendar className="w-4 h-4 mr-2" />
            Daily Brief
          </TabsTrigger>
          <TabsTrigger value="scrum" data-testid="tab-scrum">
            <Users className="w-4 h-4 mr-2" />
            Post-Scrum
          </TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">
            <BarChart3 className="w-4 h-4 mr-2" />
            Weekly Review
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <UserCog className="w-4 h-4 mr-2" />
            Recipients
          </TabsTrigger>
        </TabsList>

        {/* Daily Brief Tab */}
        <TabsContent value="brief" className="space-y-6">
          {loading && !preScrumBrief ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : preScrumBrief ? (
            <>
              {/* Brief Header */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Pre-Scrum Brief</CardTitle>
                      <CardDescription>
                        {formatDate(preScrumBrief.generated_at)} – {formatTime(preScrumBrief.generated_at)}
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {preScrumBrief.total_clients_analyzed} clients analyzed
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* Pipeline Movement */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Pipeline Movement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {preScrumBrief.pipeline_movement?.summary?.advanced_count || 0}
                      </div>
                      <div className="text-sm text-slate-600">Advancing</div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-amber-600">
                        {preScrumBrief.pipeline_movement?.summary?.stalled_count || 0}
                      </div>
                      <div className="text-sm text-slate-600">Stalled</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-red-600">
                        {preScrumBrief.pipeline_movement?.summary?.at_risk_count || 0}
                      </div>
                      <div className="text-sm text-slate-600">At Risk</div>
                    </div>
                  </div>

                  {/* At Risk Deals */}
                  {preScrumBrief.pipeline_movement?.at_risk?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-slate-700">At Risk</h4>
                      {preScrumBrief.pipeline_movement.at_risk.map((deal, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between p-3 bg-red-50 border-l-4 border-red-500 rounded"
                        >
                          <div>
                            <span className="font-medium text-slate-900">{deal.company_name}</span>
                            <span className="text-sm text-slate-500 ml-2">
                              {deal.readiness} stage • {deal.momentum} momentum
                            </span>
                          </div>
                          <Badge variant="destructive" className="text-xs">At Risk</Badge>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Advanced Deals */}
                  {preScrumBrief.pipeline_movement?.advanced?.length > 0 && (
                    <div className="space-y-2 mt-4">
                      <h4 className="text-sm font-medium text-slate-700">Advancing</h4>
                      {preScrumBrief.pipeline_movement.advanced.map((deal, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between p-3 bg-green-50 border-l-4 border-green-500 rounded"
                        >
                          <div>
                            <span className="font-medium text-slate-900">{deal.company_name}</span>
                            <span className="text-sm text-slate-500 ml-2">
                              {deal.readiness} stage
                            </span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-green-600" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Attention Allocation */}
              {(preScrumBrief.attention_allocation?.high_effort_low_progress?.length > 0 ||
                preScrumBrief.attention_allocation?.progress_low_attention?.length > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-5 h-5 text-amber-600" />
                      Attention Allocation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {preScrumBrief.attention_allocation?.high_effort_low_progress?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-700 mb-2">
                          High Effort, Limited Progress
                        </h4>
                        {preScrumBrief.attention_allocation.high_effort_low_progress.map((item, i) => (
                          <div key={i} className="p-3 bg-amber-50 border-l-4 border-amber-500 rounded mb-2">
                            <span className="font-medium">{item.company_name}</span>
                            <span className="text-sm text-slate-500 ml-2">
                              {item.events_logged} interactions • {item.momentum} momentum
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Intervention Signals */}
              {preScrumBrief.intervention_signals?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      Intervention Signals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {preScrumBrief.intervention_signals.map((signal, i) => (
                        <div
                          key={i}
                          className={`p-3 rounded border-l-4 ${
                            signal.severity === "high"
                              ? "bg-red-50 border-red-500"
                              : "bg-amber-50 border-amber-500"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{signal.company_name}</span>
                            <Badge
                              variant={signal.severity === "high" ? "destructive" : "outline"}
                              className="text-xs"
                            >
                              {signal.severity}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{signal.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Leadership Focus */}
              <Card className="border-green-200 bg-green-50/50">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Brain className="w-5 h-5 text-green-600" />
                    Suggested Leadership Focus
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {preScrumBrief.leadership_focus?.map((point, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <ArrowRight className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                        <span className="text-slate-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Effort Misallocation */}
              {effortMisallocation.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingDown className="w-5 h-5 text-amber-600" />
                      Effort Misallocation Detected
                    </CardTitle>
                    <CardDescription>
                      Opportunities where high activity isn't translating to progress
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {effortMisallocation.map((item, i) => (
                        <div key={i} className="p-4 bg-amber-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{item.company_name}</span>
                            <Badge variant="outline">{item.events_logged} events</Badge>
                          </div>
                          <p className="text-sm text-slate-600">{item.advisory_note}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600">No brief data available</p>
                <Button variant="outline" className="mt-4" onClick={fetchPreScrumBrief}>
                  Generate Brief
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Post-Scrum Tab */}
        <TabsContent value="scrum" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Capture Scrum Outcomes</CardTitle>
              <CardDescription>
                Paste your scrum notes here. Rex will extract actions, ownership references, and priority changes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Enter your scrum meeting notes here...

Example:
Good scrum today. Need to follow up with TechCorp - John mentioned they want to schedule a demo next week. Also, BankNow has gone quiet - Remi to reach out. Priority shift: focus on financial services this quarter."
                value={scrumNotes}
                onChange={(e) => setScrumNotes(e.target.value)}
                className="min-h-[200px]"
                data-testid="scrum-notes-input"
              />
              <Button onClick={handleCaptureScrum} disabled={loading || !scrumNotes.trim()}>
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Capture Outcomes
              </Button>
            </CardContent>
          </Card>

          {/* Scrum Results */}
          {scrumResult && (
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Extracted Outcomes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Actions */}
                {scrumResult.extracted?.actions?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Actions</h4>
                    <div className="space-y-2">
                      {scrumResult.extracted.actions.map((action, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                          <span>{action.description}</span>
                          <div className="flex items-center gap-2">
                            {action.client_mentioned && (
                              <Badge variant="outline">{action.client_mentioned}</Badge>
                            )}
                            <Badge
                              variant={
                                action.priority === "high"
                                  ? "destructive"
                                  : action.priority === "medium"
                                  ? "default"
                                  : "secondary"
                              }
                            >
                              {action.priority}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Priority Changes */}
                {scrumResult.extracted?.priority_changes?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Priority Changes</h4>
                    <div className="space-y-2">
                      {scrumResult.extracted.priority_changes.map((change, i) => (
                        <div key={i} className="p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                          {change.change}
                          {change.client_name && (
                            <Badge variant="outline" className="ml-2">{change.client_name}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Updates Applied */}
                {scrumResult.updates_applied?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Updates Applied</h4>
                    <div className="space-y-2">
                      {scrumResult.updates_applied.map((update, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-green-700">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>{update.client_name}: {update.update}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Weekly Review Tab */}
        <TabsContent value="weekly" className="space-y-6">
          {weeklyReview ? (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Weekly Commercial Review</CardTitle>
                      <CardDescription>
                        Week ending {weeklyReview.week_ending}
                      </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchWeeklyReview}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-slate-700">
                        {weeklyReview.metrics?.total_events || 0}
                      </div>
                      <div className="text-sm text-slate-500">Interactions</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-slate-700">
                        {weeklyReview.metrics?.active_clients || 0}
                      </div>
                      <div className="text-sm text-slate-500">Active Clients</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-slate-700">
                        {weeklyReview.metrics?.signals_detected || 0}
                      </div>
                      <div className="text-sm text-slate-500">Signals</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Movement Patterns */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    Movement Patterns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">{weeklyReview.movement_patterns?.summary}</p>
                  {weeklyReview.movement_patterns?.consistently_advancing?.length > 0 && (
                    <div className="space-y-2">
                      {weeklyReview.movement_patterns.consistently_advancing.map((client, i) => (
                        <div key={i} className="p-3 bg-green-50 rounded flex items-center justify-between">
                          <span className="font-medium">{client.company_name}</span>
                          <div className="text-sm text-slate-500">
                            {client.events} events • {client.advancing_events} advancing
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Friction Patterns */}
              {weeklyReview.friction_patterns?.friction_points?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-amber-600" />
                      Friction Patterns
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4">{weeklyReview.friction_patterns?.summary}</p>
                    <div className="space-y-2">
                      {weeklyReview.friction_patterns.friction_points.map((client, i) => (
                        <div key={i} className="p-3 bg-amber-50 rounded">
                          <span className="font-medium">{client.company_name}</span>
                          <span className="text-sm text-slate-500 ml-2">{client.pattern}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Behavioral Observations */}
              {weeklyReview.behavioral_observations?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Eye className="w-5 h-5 text-blue-600" />
                      Behavioral Observations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {weeklyReview.behavioral_observations.map((obs, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-600">
                          <ChevronRight className="w-4 h-4 mt-0.5 text-blue-500" />
                          {obs}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Strategic Signals */}
              {weeklyReview.strategic_signals?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-600" />
                      Strategic Signals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {weeklyReview.strategic_signals.map((signal, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-600">
                          <ChevronRight className="w-4 h-4 mt-0.5 text-purple-500" />
                          {signal}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Narrative Insight */}
              {weeklyReview.narrative_insight && (
                <Card className="border-slate-300 bg-slate-50">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Brain className="w-5 h-5 text-slate-700" />
                      Weekly Insight
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                      {weeklyReview.narrative_insight}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Learning Insights */}
              {learningInsights?.sufficient_data && (
                <Card className="border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                      Longitudinal Learning
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-blue-50 rounded">
                        <div className="text-lg font-bold text-blue-700">
                          {learningInsights.win_rate?.toFixed(0)}%
                        </div>
                        <div className="text-sm text-slate-600">Win Rate</div>
                      </div>
                      <div className="p-3 bg-blue-50 rounded">
                        <div className="text-lg font-bold text-blue-700">
                          {learningInsights.total_tracked}
                        </div>
                        <div className="text-sm text-slate-600">Outcomes Tracked</div>
                      </div>
                    </div>
                    {learningInsights.economic_buyer_correlation && (
                      <p className="mt-4 text-sm text-slate-600">
                        {learningInsights.economic_buyer_correlation}
                      </p>
                    )}
                    {learningInsights.champion_correlation && (
                      <p className="text-sm text-slate-600">
                        {learningInsights.champion_correlation}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600">Loading weekly review...</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Recipients Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Leadership Brief Recipients</CardTitle>
              <CardDescription>
                Manage who receives the automated daily pre-scrum brief (sent at 09:25 WAT on weekdays)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add Recipient */}
              <div className="flex gap-3">
                <Input
                  placeholder="Email address"
                  value={newRecipientEmail}
                  onChange={(e) => setNewRecipientEmail(e.target.value)}
                  className="flex-1"
                  data-testid="recipient-email-input"
                />
                <Input
                  placeholder="Name (optional)"
                  value={newRecipientName}
                  onChange={(e) => setNewRecipientName(e.target.value)}
                  className="w-48"
                  data-testid="recipient-name-input"
                />
                <Button onClick={handleAddRecipient} data-testid="add-recipient-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>

              {/* Recipients List */}
              <div className="space-y-2">
                {recipients.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No recipients configured</p>
                    <p className="text-sm">Add recipients to enable automated briefs</p>
                  </div>
                ) : (
                  recipients.map((recipient, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <Mail className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">
                            {recipient.name || recipient.email}
                          </div>
                          {recipient.name && (
                            <div className="text-sm text-slate-500">{recipient.email}</div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveRecipient(recipient.email)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Schedule Info */}
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-slate-900">Automated Schedule</h4>
                  <p className="text-sm text-slate-600 mt-1">
                    Daily pre-scrum brief: <strong>09:25 WAT</strong> (Mon-Fri)
                  </p>
                  <p className="text-sm text-slate-600">
                    Weekly commercial review: <strong>17:00 WAT</strong> (Friday)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
